/**
 * Normalise exponent notation so superscripts look the same everywhere.
 *
 * Converts caret notation written by authors or the AI (e.g. "(1 + r)^n",
 * "1.4^(1/4)", "10^-3") into real Unicode superscript characters
 * ("(1 + r)ⁿ", "1.4⁽¹⁄⁴⁾", "10⁻³") so the platform renders A³ rather than A^3.
 */

const MAP: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
  '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
  '+': '⁺', '-': '⁻', '−': '⁻', '=': '⁼',
  '(': '⁽', ')': '⁾', '/': '⁄', '.': '·', ',': '˒',
  n: 'ⁿ', i: 'ⁱ', t: 'ᵗ', x: 'ˣ', y: 'ʸ', a: 'ᵃ', b: 'ᵇ', c: 'ᶜ',
  d: 'ᵈ', e: 'ᵉ', h: 'ʰ', k: 'ᵏ', m: 'ᵐ', p: 'ᵖ', r: 'ʳ', s: 'ˢ',
  T: 'ᵀ', N: 'ᴺ',
};

function convert(body: string): string | null {
  let out = '';
  for (const ch of body) {
    const mapped = MAP[ch];
    if (!mapped) return null; // leave untouched if we cannot represent it
    out += mapped;
  }
  return out;
}

/** Turn `^...` exponents into Unicode superscripts. Safe on empty/undefined. */
export function sup(text?: string | null): string {
  if (!text) return text ?? '';
  if (!text.includes('^')) return text;

  return (
    text
      // Markdown footnote refs like [^1] are not exponents - drop them.
      .replace(/\[\^[^\]]*\]/g, '')
      // ^{...} or ^(...) groups, then bare runs of digits/letters/signs.
      .replace(/\^\{([^}]{1,12})\}/g, (m, body: string) => convert(body) ?? m)
      .replace(/\^\(([^)]{1,12})\)/g, (m, body: string) => {
        const inner = convert(body);
        return inner ? `⁽${inner}⁾` : m;
      })
      .replace(/\^([-−+]?[0-9]+(?:\.[0-9]+)?|[A-Za-z])/g, (m, body: string) => convert(body) ?? m)
  );
}

export default sup;
